import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/rn3";
import { useAuth } from "@/lib/auth";
import { Shield, Search, RefreshCw, AlertTriangle, CheckCircle2, Lock } from "lucide-react";
import { useT } from "@/lib/i18n";

const ACTION_VALUES = [
  "all",
  "login_success",
  "login_failed",
  "login_2fa_required",
  "login_2fa_verified",
  "login_2fa_failed",
  "logout",
  "register",
  "2fa_setup_completed",
  "2fa_disabled",
  "2fa_recovery_used",
];

const ACTION_COLORS = {
  login_success: "text-green-700 bg-green-50 border-green-200",
  login_2fa_verified: "text-green-700 bg-green-50 border-green-200",
  "2fa_setup_completed": "text-green-700 bg-green-50 border-green-200",
  register: "text-blue-700 bg-blue-50 border-blue-200",
  logout: "text-gray-700 bg-gray-50 border-gray-200",
  login_failed: "text-red-700 bg-red-50 border-red-200",
  login_2fa_failed: "text-red-700 bg-red-50 border-red-200",
  "2fa_recovery_used": "text-amber-800 bg-amber-50 border-amber-300",
  "2fa_disabled": "text-amber-800 bg-amber-50 border-amber-300",
};

const ActivityTab = () => {
  const t = useT();
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [action, setAction] = useState("all");
  const [days, setDays] = useState(30);
  const [emailFilter, setEmailFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (action !== "all") params.set("action", action);
      params.set("days", days);
      params.set("limit", "200");
      const [a, s] = await Promise.all([
        api.get(`/admin/activity?${params.toString()}`),
        api.get(`/admin/activity/stats?days=${days}`),
      ]);
      setItems(a.data.items || []);
      setTotal(a.data.total || 0);
      setStats(s.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user && user.role !== "admin") { nav("/portal", { replace: true }); return; }
    load();
  }, [action, days]);

  const filtered = items.filter((e) => {
    if (!emailFilter.trim()) return true;
    const needle = emailFilter.trim().toLowerCase();
    return (e.user_email || "").toLowerCase().includes(needle) || (e.ip_address || "").includes(needle);
  });

  return (
    <div className="rn3-container py-10" data-testid="activity-page">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="rn3-eyebrow flex items-center gap-2"><Shield className="w-4 h-4" /> {t("admin.activity.eyebrow")}</div>
          <h1 className="font-serif-display text-4xl mt-3">{t("admin.activity.heading")}</h1>
          <p className="text-sm text-[#737373] mt-2">{t("admin.activity.description")}</p>
        </div>
        <button onClick={load} className="rn3-btn-gold" data-testid="activity-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {t("admin.activity.refresh")}
        </button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="activity-stats">
          <div className="rn3-card">
            <div className="flex items-center gap-2 rn3-eyebrow"><CheckCircle2 className="w-4 h-4 text-green-600" /> {t("admin.activity.stat.successful")}</div>
            <div className="font-serif-display text-3xl mt-2">{stats.successful_logins}</div>
            <div className="text-xs text-[#737373]">{t("admin.activity.last_days").replace("{n}", String(stats.days))}</div>
          </div>
          <div className="rn3-card">
            <div className="flex items-center gap-2 rn3-eyebrow"><AlertTriangle className="w-4 h-4 text-red-600" /> {t("admin.activity.stat.failed")}</div>
            <div className="font-serif-display text-3xl mt-2">{stats.failed_logins}</div>
            <div className="text-xs text-[#737373]">{t("admin.activity.last_days").replace("{n}", String(stats.days))}</div>
          </div>
          <div className="rn3-card">
            <div className="flex items-center gap-2 rn3-eyebrow"><Lock className="w-4 h-4 text-[#8B1C22]" /> {t("admin.activity.stat.locked")}</div>
            <div className="font-serif-display text-3xl mt-2">{stats.currently_locked}</div>
            <div className="text-xs text-[#737373]">{t("admin.activity.lockouts_active")}</div>
          </div>
          <div className="rn3-card">
            <div className="rn3-eyebrow">{t("admin.activity.top_action")}</div>
            <div className="font-serif-display text-xl mt-2 truncate">
              {stats.by_action?.[0]?.action ? t(`admin.activity.action.${stats.by_action[0].action}`) : "—"}
            </div>
            <div className="text-xs text-[#737373]">{t("admin.activity.events_count").replace("{n}", String(stats.by_action?.[0]?.count || 0))}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mt-8 mb-4" data-testid="activity-filters">
        <div>
          <label className="rn3-eyebrow block mb-1">{t("admin.activity.filter.action")}</label>
          <select value={action} onChange={(e) => setAction(e.target.value)} className="rn3-input" data-testid="filter-action">
            {ACTION_VALUES.map((v) => <option key={v} value={v}>{t(`admin.activity.action.${v}`)}</option>)}
          </select>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-1">{t("admin.activity.filter.last")}</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rn3-input" data-testid="filter-days">
            <option value={1}>{t("admin.activity.range.24h")}</option>
            <option value={7}>{t("admin.activity.range.7d")}</option>
            <option value={30}>{t("admin.activity.range.30d")}</option>
            <option value={90}>{t("admin.activity.range.90d")}</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="rn3-eyebrow block mb-1">{t("admin.activity.filter.search")}</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
            <input
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              className="rn3-input pl-10"
              placeholder={t("admin.activity.search_placeholder")}
              data-testid="filter-search"
            />
          </div>
        </div>
        <div className="text-xs text-[#737373] ml-auto">
          {t("admin.activity.showing").replace("{n}", String(filtered.length)).replace("{total}", String(total))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-[#E5E5E5]" data-testid="activity-table">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-[10px] uppercase tracking-widest text-[#737373]">
            <tr>
              <th className="text-left p-3 font-medium">{t("admin.activity.col.when")}</th>
              <th className="text-left p-3 font-medium">{t("admin.activity.col.action")}</th>
              <th className="text-left p-3 font-medium">{t("admin.activity.col.user")}</th>
              <th className="text-left p-3 font-medium">{t("admin.activity.col.ip")}</th>
              <th className="text-left p-3 font-medium">{t("admin.activity.col.user_agent")}</th>
              <th className="text-left p-3 font-medium">{t("admin.activity.col.details")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-[#737373]">{t("admin.activity.empty")}</td></tr>
            ) : filtered.map((e) => (
              <tr key={e.id} className="border-t border-[#E5E5E5] hover:bg-[#FDFBF7]" data-testid={`activity-row-${e.id}`}>
                <td className="p-3 whitespace-nowrap text-xs text-[#404040]">
                  {new Date(e.timestamp).toLocaleString()}
                </td>
                <td className="p-3 whitespace-nowrap">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${ACTION_COLORS[e.action] || "text-[#404040] bg-[#FAFAFA] border-[#E5E5E5]"}`}>
                    {e.action}
                  </span>
                </td>
                <td className="p-3 whitespace-nowrap">
                  <div className="font-medium text-xs">{e.user_email || "—"}</div>
                  {e.user_role && <div className="text-[10px] text-[#737373] uppercase tracking-widest">{e.user_role}</div>}
                </td>
                <td className="p-3 whitespace-nowrap font-mono text-xs">{e.ip_address || "—"}</td>
                <td className="p-3 text-xs text-[#737373] max-w-xs truncate" title={e.user_agent}>{e.user_agent || "—"}</td>
                <td className="p-3 text-xs">
                  {e.details && Object.keys(e.details).length > 0 ? (
                    <code className="text-[10px] text-[#404040] break-all">{JSON.stringify(e.details)}</code>
                  ) : <span className="text-[#737373]">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivityTab;
